import { describesSizeInMetres } from './plantProperties';

/**
 * Filtering the catalogue on the properties the cards already print (SPEC-011,
 * recomposed by SPEC-012).
 *
 * A filter option is a BAND, and the bands come from the `score` the ingest
 * already assigned (SPEC-002). Water score 1 covers `veryLow`, `low` and
 * `lowOnceEstablished` — precisely the set a gardener means by "low water".
 * Filtering on the label key instead would offer two options both reading "Low",
 * because the `.short` vocabulary only deduplicates after translation, and would
 * drop the four very-low plants out of a low-water filter.
 *
 * Every band therefore names an EXISTING value key — `plants.water.low` — and
 * derives its own copy from it: `.short` for the tile's label, `.caption` for the
 * line under it. No new vocabulary, and no second set of words to drift away from
 * the one on the card.
 *
 * Within a group the options are OR-ed; across groups they are AND-ed.
 */

/**
 * How many steps each scored property has, which is what the card's meter counts
 * out (SPEC-012 D-2).
 *
 * This is the SCALE the ingest scores against, not the set of options offered
 * below: no plant in this catalogue is a three-droplet plant, and on a product
 * about climate-resilient planting that empty third droplet is a fact worth
 * drawing rather than a gap to hide by shortening the meter.
 */
export const PROPERTY_SCALE_MAX = 3;

/**
 * Bands, named by the value key they borrow their copy from.
 *
 * The third water band was the case this comment used to describe as hypothetical
 * — the scale had a step the catalogue had no plant on. Fittonia put one there,
 * arrived with a `plants.water.high` label key of its own, and the band was the
 * one line promised. The same went for the third maintenance band beneath it.
 *
 * A band is not decoration: `bandValueOf` answers null for a score no band
 * claims, so a plant on an undeclared step matches no option in its group and
 * quietly disappears from a filtered shelf instead of failing loudly.
 */
const SCORE_BANDS = {
    water: [
        { value: 'low', score: 1, key: 'plants.water.low' },
        { value: 'moderate', score: 2, key: 'plants.water.lowToModerate' },
        { value: 'high', score: 3, key: 'plants.water.high' },
    ],
    sun: [
        { value: 'shade', score: 1, key: 'plants.sun.partShadeToShade' },
        { value: 'sunOrShade', score: 2, key: 'plants.sun.fullSunPartShade' },
        { value: 'fullSun', score: 3, key: 'plants.sun.fullSun' },
    ],
    soil: [
        { value: 'any', score: 1, key: 'plants.soil.adaptable' },
        { value: 'wellDrained', score: 2, key: 'plants.soil.wellDrained' },
        { value: 'sharplyDrained', score: 3, key: 'plants.soil.sharplyDrained' },
    ],
    maintenance: [
        { value: 'easy', score: 1, key: 'plants.maintenance.easy' },
        { value: 'some', score: 2, key: 'plants.maintenance.lowOnceEstablished' },
        { value: 'regular', score: 3, key: 'plants.maintenance.moderate' },
    ],
};

/**
 * Size is two measurements rather than a score, so it bands on the lower one.
 *
 * Four bands, at the approved sheet's boundaries. The top one is not a fourth
 * threshold: it is `describesSizeInMetres`, the same 300cm the card already uses
 * to choose between a sprout and a tree pictogram. One threshold, owned by
 * `plantProperties`, so a plant cannot be filtered as very tall while being drawn
 * as small.
 */
const SMALL_PLANT_MAX_CM = 50;
const MEDIUM_PLANT_MAX_CM = 120;

const SIZE_BANDS = [
    { value: 'small', key: 'plants.size.band.small' },
    { value: 'medium', key: 'plants.size.band.medium' },
    { value: 'tall', key: 'plants.size.band.tall' },
    { value: 'veryTall', key: 'plants.size.band.veryTall' },
];

export function sizeBandOf(size) {
    if (describesSizeInMetres(size)) {
        return 'veryTall';
    }

    if (size?.minCm < SMALL_PLANT_MAX_CM) {
        return 'small';
    }

    return size?.minCm < MEDIUM_PLANT_MAX_CM ? 'medium' : 'tall';
}

/**
 * The resilience filters of the source spec's §4.2 that this content model
 * actually supports. Deer resistance and native range are not among them, and an
 * option no pack can ever satisfy is a dead end rather than a feature.
 */
const OFFERED_TRAITS = [
    'plants.trait.pollinatorFriendly',
    'plants.trait.droughtTolerant',
    'plants.trait.coldHardy',
];

/**
 * A band's three message keys, derived from the one value key it names.
 *
 * `.short` is the word the card already prints, `.caption` the line under it in
 * the sheet — "6+ hours" beside "Full sun". Deriving rather than storing is what
 * makes a band one line to declare and impossible to declare inconsistently.
 *
 * The score rides along because the water tile draws the same three-droplet meter
 * the card does, at the band's own level. Size has no score, and says so.
 */
function optionFrom({ value, key, score = null }) {
    return { value, score, labelKey: `${key}.short`, captionKey: `${key}.caption` };
}

function bandOptions(groupKey) {
    return SCORE_BANDS[groupKey].map(optionFrom);
}

/**
 * Which band this plant falls in, for one property.
 *
 * The card asks so it can draw the band's glyph, and the filter asks so it can
 * decide whether the plant matches — one function, so a plant cannot be drawn
 * with a cloud and filtered as full sun.
 */
export function bandValueOf(plant, groupKey) {
    if (groupKey === 'size') {
        return sizeBandOf(plant?.properties?.size);
    }

    const score = plant?.properties?.[groupKey]?.score;

    return SCORE_BANDS[groupKey]?.find((band) => band.score === score)?.value ?? null;
}

function matchesBand(groupKey) {
    return (plant, value) => bandValueOf(plant, groupKey) === value;
}

/**
 * The groups, in the order the sheet renders them: the four decision properties
 * first, because they are the four the card is built around, then how much work
 * a plant is, then what it is good for.
 *
 * There is no Category group. Category is the axis the shelves themselves are
 * built on (SPEC-012 D-1) — filtering to one category from in here would ask a
 * gardener to operate a control to reach a shelf that is already on screen.
 */
export const FILTER_GROUPS = [
    { key: 'water', labelKey: 'plants.property.water', options: bandOptions('water'), matches: matchesBand('water') },
    { key: 'sun', labelKey: 'plants.property.sun', options: bandOptions('sun'), matches: matchesBand('sun') },
    { key: 'soil', labelKey: 'plants.property.soil', options: bandOptions('soil'), matches: matchesBand('soil') },
    {
        key: 'size',
        labelKey: 'plants.property.size',
        options: SIZE_BANDS.map(optionFrom),
        matches: matchesBand('size'),
    },
    {
        key: 'maintenance',
        labelKey: 'plants.property.maintenance',
        options: bandOptions('maintenance'),
        matches: matchesBand('maintenance'),
    },
    {
        key: 'traits',
        labelKey: 'plants.filter.group.traits',
        // A trait is its own key, so the derivation above works unchanged.
        options: OFFERED_TRAITS.map((traitValue) => ({
            value: traitValue,
            labelKey: traitValue,
            captionKey: `${traitValue}.caption`,
        })),
        matches: (plant, value) => (plant.traits || []).includes(value),
    },
];

/**
 * The one group the sheet draws as switches rather than as tiles (SPEC-012 D-5).
 *
 * A strength is a yes/no a plant either has or does not, and the approved sheet
 * draws those as a labelled list with a switch each. The four properties above
 * are a choice between bands, which is a row of tiles. Same filter object
 * underneath; two presentations, because they are two different questions.
 */
export const SWITCH_GROUP_KEY = 'traits';

export const FILTER_GROUP_KEYS = FILTER_GROUPS.map((group) => group.key);

/** The unfiltered catalogue. Frozen: every operation below returns a new object. */
export const NO_FILTERS = Object.freeze(
    Object.fromEntries(FILTER_GROUP_KEYS.map((key) => [key, Object.freeze([])])),
);

/**
 * The chips above the shelves (source spec §5).
 *
 * Each one is a `(group, value)` pair pointing into the SAME filter object the
 * sheet edits, which is what keeps the two synchronised rather than merely in
 * step: "Low water" on the chip row and "Low" under Water in the sheet are one
 * checkbox drawn twice.
 *
 * Three, and deliberately not a projection of the whole model — the row is for
 * the highest-frequency criteria, so it must not grow when a group does.
 */
export const QUICK_FILTERS = [
    { key: 'lowWater', groupKey: 'water', value: 'low', labelKey: 'plants.trait.lowWater' },
    { key: 'fullSun', groupKey: 'sun', value: 'fullSun', labelKey: 'plants.trait.fullSun' },
    {
        key: 'pollinators',
        groupKey: 'traits',
        value: 'plants.trait.pollinatorFriendly',
        labelKey: 'plants.trait.pollinatorFriendly',
    },
];

function groupByKey(groupKey) {
    return FILTER_GROUPS.find((group) => group.key === groupKey) || null;
}

export function isFilterActive(filters, groupKey, value) {
    return (filters?.[groupKey] || []).includes(value);
}

export function toggleFilterValue(filters, groupKey, value) {
    if (!groupByKey(groupKey)) {
        return filters;
    }

    const selected = filters?.[groupKey] || [];

    return {
        ...filters,
        [groupKey]: selected.includes(value)
            ? selected.filter((entry) => entry !== value)
            : [...selected, value],
    };
}

export function countActiveFilters(filters) {
    return FILTER_GROUP_KEYS.reduce((total, key) => total + (filters?.[key]?.length || 0), 0);
}

export function matchesFilters(plant, filters) {
    return FILTER_GROUPS.every((group) => {
        const selected = filters?.[group.key] || [];

        return selected.length === 0 || selected.some((value) => group.matches(plant, value));
    });
}

export function filterPlants(plants, filters) {
    return countActiveFilters(filters) === 0
        ? plants
        : plants.filter((plant) => matchesFilters(plant, filters));
}

/**
 * The groups worth offering, given the catalogue in hand.
 *
 * An option no plant satisfies is dropped, and a group left with fewer than two
 * options is dropped whole — a filter with one choice is a label. This is what
 * keeps a water band nothing is on out of the sheet without naming it, and what
 * makes adding a plant a content operation rather than a code change.
 */
export function listFilterGroups(plants) {
    return FILTER_GROUPS
        .map(({ key, labelKey, options, matches }) => ({
            key,
            labelKey,
            options: options
                .map((option) => ({
                    ...option,
                    count: plants.filter((plant) => matches(plant, option.value)).length,
                }))
                .filter((option) => option.count > 0),
        }))
        .filter((group) => group.options.length > 1);
}
